import React, { useEffect, useState } from "react";
import { getDataWithFilterUrl } from "../../controllers/APIController";
import {
  Table,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  TableColumn,
  Button,
  useDisclosure,
  Image,
  Pagination,
  Input,
} from "@nextui-org/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import DownloadIcon from "../../Images/Icons/download.svg";

import BreadCrumbsPage from "../../components/BreadCrumbsPage";
import NullData from "../../components/ErrorPage/NullData";
import { PageLoader } from "../../components/Loader";
import AddEvidenceModal from "./AddEvidenceModal";

const Evidence = () => {
  const [reRenderPage, setReRenderPage] = useState(true);

  const [filterEvidenceData, setFilterEvidenceData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  const [isLoading, setIsLoading] = useState(true);
  const { isOpen, onOpen, onOpenChange } = useDisclosure();

  const UserLogged = JSON.parse(sessionStorage.getItem("userData"));
  const OrgInfo = UserLogged && UserLogged.user.LastOrgId;
  const orgFilterUrl = "filters[Organization][id][$eqi]=" + OrgInfo;

  const downloadHandler = (url) => {
    window.open(url);
  };

  useEffect(() => {
    getDataWithFilterUrl("evidences", orgFilterUrl)
      .then(async (data) => {
        const dataList = data?.data || [];
        setFilterEvidenceData(dataList);
        setIsLoading(false);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [reRenderPage, orgFilterUrl]);

  // Filter data based on search term
  const filteredData = filterEvidenceData.filter((item) => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    
    return (
      item?.attributes?.Name?.toLowerCase().includes(searchLower) ||
      item?.attributes?.Description?.toLowerCase().includes(searchLower)
    );
  });

  const ItemPerPage = 10;
  const totalPage = Math.ceil(filteredData?.length / ItemPerPage);

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Page Header */}
      <div className="bg-white border-b border-neutral-200 px-8 py-6 shadow-sm">
        <div className="flex items-center justify-between max-w-full">
          <div>
            <h1 className="text-2xl font-semibold text-neutral-900">Evidences</h1>
            <p className="text-sm text-neutral-600 mt-1">View your evidences that backs your ESG reports</p>
          </div>
          <Button
            color="primary"
            className="bg-primary-600 hover:bg-primary-700 text-white px-6 font-medium"
            startContent={
              <lord-icon
                src="https://cdn.lordicon.com/smwmetfi.json"
                colors="primary:#ffffff,secondary:#ffffff"
                trigger="hover"
                
                style={{ width: "20px", height: "20px" }}
              ></lord-icon>
            }
            onClick={() => onOpen()}
          >
            Add an evidence file
          </Button>
        </div>
      </div>

      <BreadCrumbsPage>
        <div className="max-w-7xl mx-auto px-8 py-8">
          {isLoading ? (
            <PageLoader
              loadingText="Loading Evidences..."
              description="Fetching evidence files and organization data"
              variant="default"
            />
          ) : (
            <>
              {/* Evidence Management Section */}
            <div className="bg-white rounded-lg border border-neutral-200 shadow-sm p-6 mb-8">
              <div>
                <h2 className="text-lg font-semibold text-neutral-900">Evidence Management</h2>
                <p className="text-sm text-neutral-600 mt-1">
                  Upload and manage evidence files that support your ESG reporting. These documents provide backing for your sustainability metrics and compliance requirements.
                </p>
              </div>
            </div>

            {/* Evidence Repository Section */}
            <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-neutral-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900">Evidence Repository</h3>
                    <p className="text-sm text-neutral-600 mt-1">Browse and manage uploaded evidence files</p>
                  </div>
                  <div className="w-80">
                    <Input
                      placeholder="Search evidences..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      startContent={<FontAwesomeIcon icon={faSearch} className="text-neutral-400 text-sm" />}
                      size="sm"
                    />
                  </div>
                </div>
              </div>
              <div className="p-6">
                {filteredData && filteredData?.length > 0 ? (
                  <>
                    <Table
                      removeWrapper
                      aria-label="Example static collection table"
                    >
                      <TableHeader>
                        <TableColumn>Name</TableColumn>
                        <TableColumn>Description</TableColumn>
                        <TableColumn>Reference To</TableColumn>
                        <TableColumn>Upload Date</TableColumn>
                        <TableColumn>Actions</TableColumn>
                      </TableHeader>
                      <TableBody
                        emptyContent={<NullData title={`No Evidence found.`} />}
                      >
                        {filteredData &&
                          Array.isArray(filteredData) &&
                          filteredData.map((item, index) => (
                            <TableRow key={item.id}>
                              <TableCell>
                                <div className="flex gap-2">
                                  <lord-icon
                                    src="https://cdn.lordicon.com/rbbnmpcf.json"
                                    trigger="hover"
                                    
                                    style={{ width: "20px", height: "20px" }}
                                  ></lord-icon>
                                  {item?.attributes?.Name}
                                </div>
                              </TableCell>
                              <TableCell>
                                {item?.attributes?.Description}
                              </TableCell>
                              <TableCell>
                                {item?.attributes?.ReferenceList?.data?.length > 0
                                  ? item?.attributes?.ReferenceList?.data?.length
                                  : "---"}
                              </TableCell>
                              <TableCell>
                                {new Date(
                                  item?.attributes?.createdAt
                                ).toLocaleDateString()}
                              </TableCell>
                              <TableCell>
                                <div
                                  className="flex items-center gap-2 cursor-pointer"
                                  onClick={() =>
                                    downloadHandler(item?.attributes?.EvidenceUrl)
                                  }
                                >
                                  <Image
                                    width={20}
                                    alt="NextUI hero Image"
                                    src={DownloadIcon}
                                  />
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                    {filteredData?.length > 0 && (
                      <div className="w-full mt-8 flex items-center justify-center">
                        <Pagination
                          showControls
                          total={totalPage}
                          color="secondary"
                          page={currentPage}
                          onChange={setCurrentPage}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <NullData />
                )}
              </div>
            </div>
            </>
          )}
        </div>
      </BreadCrumbsPage>

      <AddEvidenceModal
        onSubmitHandler={1}
        id={1}
        isOpen={isOpen}
        onOpen={onOpen}
        onOpenChange={onOpenChange}
        setReRenderPage={setReRenderPage}
        pageContext="General Evidence"
      />
    </div>
  );
};

export default Evidence;
